import React from 'react';
import styled from 'styled-components';
import { Trans } from 'react-i18next';

import Button from 'components/button';
import CancelDialog from './cancel-showing-dialog';
import AccessCodeModal from './access-code-modal';
import { getTourLocks } from '../hooks/useGenerateCode';
import { LaunchAppButton } from '../components/promotion';

const STATUS_ENUM = {
  incomplete: 'INCOMPLETE',
  pending: 'REQUESTED',
  accepted: 'ACCEPTED',
  denied: 'DENIED',
  canceled: 'CANCELLED',
  unit_unavailable: 'UNAVAILABLE'
};

export default ({ showing, navigate, refreshList }) => {
  const [showCancelDialog, setShowCancelDialog] = React.useState(false);
  const [showAccessCodeDialog, setShowAccessCodeDialog] = React.useState(false);

  const location = showing?.location || {};
  const image =
    location?.applicationConfig?.coverPhoto?.[0] ||
    location?.applicationConfig?.marketingImages?.[0];

  const showApplyButton =
    showing.allowShowingPromotion || showing.existingLeaseApplications?.length > 0;

  const isUnitUnavailable = showing.archived && showing.archiveReason === 'unit_unavailable';

  const cancelAction = () => setShowCancelDialog(true);
  const continueAction = () => navigate(`app/showing/${showing._id}/summary`);
  const findSimilarAction = () => navigate(`app/showing/create?fromShowingId=${showing._id}`);

  let status = showing.submitted ? 'pending' : 'incomplete';
  if (showing.status) {
    status = showing.status.isApproved ? 'accepted' : 'denied';
  }
  if (isUnitUnavailable) {
    status = 'unit_unavailable';
  }

  return (
    <Wrapper>
      {showCancelDialog && (
        <CancelDialog
          showing={showing}
          onClose={({ cancelled } = {}) => {
            setShowCancelDialog(false);
            if (cancelled) {
              refreshList();
            }
          }}
        />
      )}
      {showAccessCodeDialog && (
        <AccessCodeModal showing={showing} onClose={() => setShowAccessCodeDialog(false)} />
      )}
      <div className="body-content">
        <Logo image={image} />
        <Content>
          <Title>{location.address?.formattedAddress || location.name}</Title>
          <DataRow>
            <UnitInfo>
              {!!location.bedCount && <div>{location.bedCount} BD</div>}
              {!!location.bathCount && <div>{location.bathCount} BA</div>}
              {!!location?.squareFeet && <div>{location.squareFeet} SF</div>}
            </UnitInfo>
            <PropertyInfo>
              {!!location?.applicationConfig?.marketRentValue && (
                <div>
                  <span>
                    <Trans>Rent</Trans>
                  </span>
                  <div>
                    <strong>
                      {(location?.applicationConfig?.marketRentValue / 100).renderPrettyCurrency(
                        false
                      )}
                    </strong>
                    /mo
                  </div>
                </div>
              )}
              {!!location.applicationConfig?.availableDate && (
                <div>
                  <span>
                    <Trans>Available</Trans>
                  </span>
                  <div>
                    <strong>{location.applicationConfig?.availableDate}</strong>
                  </div>
                </div>
              )}
            </PropertyInfo>
          </DataRow>
        </Content>
        <TourStatus>
          <Row>
            <span>
              <Trans>Tour Status</Trans>
            </span>
            <div className={status}>
              <strong>{STATUS_ENUM[status]}</strong>
            </div>
          </Row>

          {!!showing.status?.isApproved && getTourLocks(showing.location).length > 0 && (
            <GetCode variant="primaryFocused" onClick={() => setShowAccessCodeDialog(true)}>
              <Trans i18nKey="showing.actions.accessCode">GET ACCESS CODE</Trans>
            </GetCode>
          )}
          <p>
            Requested: {showing.createdAt?.toLocaleDateString()} at{' '}
            {showing.createdAt?.toLocaleTimeString()}
          </p>
        </TourStatus>

        {isUnitUnavailable ? (
          <>
            <UnavailableNotice>
              <Trans i18nKey="showing.unitUnavailable.notice">
                This unit is no longer available. Your verification and qualification answers are
                saved — you only need to choose a new home and time.
              </Trans>
            </UnavailableNotice>
            <ActionRow>
              <div>
                <FindSimilarButton variant="primaryFocused" onClick={findSimilarAction}>
                  <Trans i18nKey="showing.actions.findSimilar">Find Similar Homes</Trans>
                </FindSimilarButton>
              </div>
            </ActionRow>
          </>
        ) : (
          <ActionRow>
            <div>
              {showing.submitted ? (
                <Button variant="primary" onClick={continueAction}>
                  <Trans i18nKey="showing.actions.update">Update</Trans>
                </Button>
              ) : (
                <Button variant="primary" onClick={continueAction}>
                  <Trans i18nKey="showing.actions.completeRequest">Continue</Trans>
                </Button>
              )}
              <Button variant="secondary" onClick={cancelAction} width={80}>
                <Trans i18nKey="showing.actions.cancel">Cancel</Trans>
              </Button>
            </div>
          </ActionRow>
        )}
      </div>
      {showApplyButton ? (
        <LaunchAppButton showing={showing} navigate={navigate} className="footer-content" />
      ) : (
        <></>
      )}
    </Wrapper>
  );
};

const GetCode = styled(Button)`
  width: 100%;
  background-color: #00a1ff;
  color: #fff;
  padding: 12px;
  font-weight: bold;
  border-radius: 4px;
  margin-top: 10px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  > span {
    color: #71828f;
    text-transform: uppercase;
    font-size: 0.9em;
  }
  > div {
    font-size: 1.2em;
    text-transform: uppercase;
    &.pending {
      color: #ff8200;
    }
    &.incomplete {
      color: #00a1ff;
    }
    &.accepted {
      color: #4aac00;
    }
    &.denied,
    &.cancelled {
      color: #d20000;
    }
    &.unit_unavailable {
      color: #e67e00;
    }
  }
  > p {
    margin: 5px 0 0;
    padding: 0;
    color: #71828f;
    font-size: 0.8em;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  position: relative;
  overflow: hidden;
  .body-content {
    flex-grow: 1;
    padding: 20px;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
  }
  .footer-content {
    border-top: solid 1px #dfe7ed;
    background-color: #f9f9f9;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  color: #333;
  height: 200px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 1.4em;
  margin-bottom: 10px;
  padding: 20px 20px 0;
  color: #333;
  position: relative;
  z-index: 2;
`;

const Logo = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  background-image: url(${({ image }) => image}), url('/assets/location_placeholder.png');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7));
  }
`;

const DataRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px;
  gap: 15px;
  position: relative;
  z-index: 2;
`;

const UnitInfo = styled.div`
  display: flex;
  gap: 15px;
  > div {
    color: #333;
    font-weight: 600;
    font-size: 1em;
  }
`;

const PropertyInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  > div {
    display: flex;
    flex-flow: column;
    > span {
      color: #666;
      text-transform: uppercase;
      font-size: 0.9em;
      font-weight: 500;
    }
    > div {
      font-size: 1.2em;
      color: #333;
      font-weight: 600;
    }
  }
`;

const TourStatus = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-flow: column;
  background: rgba(255, 255, 255, 0.95);
  // padding: 15px 20px;
  color: #333;
  margin-top: auto;
  > p {
    color: #666;
  }
`;

const UnavailableNotice = styled.p`
  margin: 12px 0 0;
  padding: 10px 12px;
  background-color: #fff8f0;
  border-left: 3px solid #e67e00;
  color: #7a4200;
  font-size: 0.85em;
  line-height: 1.5;
  border-radius: 0 4px 4px 0;
`;

const FindSimilarButton = styled(Button)`
  width: 100%;
  background-color: #e67e00;
  border-color: #e67e00;
  color: #fff;
  font-weight: 900;
  text-transform: uppercase;
  border-radius: 25px;
  &:hover {
    background-color: #cc6f00;
    border-color: #cc6f00;
  }
`;

const ActionRow = styled.div`
  flex-grow: 1;
  margin: 20px 0 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-grow: 1;
  }

  button {
    padding: 5px 10px !important;
    font-size: 0.81em;
    width: auto;

    &.primary {
      font-weight: 900;
      text-transform: uppercase;
      border-radius: 25px;
      color: #00a1ff;
      border: solid 1px #00a1ff;
      background-color: #fff;
      &:hover {
        background-color: #00a1ff;
        color: #fff;
      }
    }
    &.secondary {
      text-transform: uppercase;
      font-weight: 900;
      border-radius: 25px;
      border: solid 1px #d20000;
      color: #d20000;
      background-color: #fff;
      &:hover {
        background-color: #d20000;
        color: #fff;
      }
    }
    &.primaryFocused {
      font-weight: 900;
      text-transform: uppercase;
      border-radius: 25px;
      color: #fff;
      border: solid 1px #00a1ff;
      background-color: #00a1ff;
      &:hover {
        background-color: #fff;
        color: #00a1ff;
      }
    }
    position: relative;
    z-index: 2;
  }
`;
